import { useState } from 'react';
import { App, Button, Card, ColorPicker, Form, Input, InputNumber, Modal, Popconfirm, Select, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, HolderOutlined, PlusOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PipelineItem, StageItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { useAddStage, useCreatePipeline, useDeletePipeline, useDeleteStage, usePipelines, useReorderStages, useUpdatePipeline, useUpdateStage } from '../../features/deals/api';
import { EmptyState, LoadError } from '../../components/states';
import { plural } from '../../utils/format';

const KIND_COLOR = { OPEN: 'processing', WON: 'success', LOST: 'error' } as const;
const KIND_LABEL = { OPEN: 'Open', WON: 'Won', LOST: 'Lost' } as const;

function SortableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props['data-row-key'] });
  const style: React.CSSProperties = { ...props.style, transform: CSS.Translate.toString(transform), transition, ...(isDragging ? { position: 'relative', zIndex: 10, background: '#fafafa' } : {}) };
  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
}

export function PipelinesPage() {
  const { message } = App.useApp();
  const pipelines = usePipelines();
  const create = useCreatePipeline();
  const update = useUpdatePipeline();
  const remove = useDeletePipeline();
  const [newOpen, setNewOpen] = useState(false);
  const [form] = Form.useForm<{ name: string }>();

  return (
    <div className="page">
      <PageHeader
        title="Pipelines"
        subtitle="Each pipeline has its own stages. Deals move through stages on the Pipeline board; Won and Lost stages close a deal."
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setNewOpen(true); }}>New pipeline</Button>}
      />
      {pipelines.isLoading && <Card><Skeleton active /></Card>}
      {pipelines.isError && <LoadError error={pipelines.error} onRetry={pipelines.refetch} />}
      {pipelines.data && !pipelines.data.length && (
        <Card>
          <EmptyState description="No pipelines yet. Create one to start tracking deals." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setNewOpen(true); }}>New pipeline</Button>} />
        </Card>
      )}
      {(pipelines.data ?? []).map((p) => (
        <Card
          key={p.id}
          style={{ marginBottom: 24 }}
          title={
            <CardTitle
              title={
                <Space size={10} wrap>
                  <Typography.Text strong style={{ fontSize: 17 }} editable={{ onChange: (name) => name.trim() && update.mutateAsync({ id: p.id, name: name.trim() }).catch((e) => message.error(errorMessage(e))) }}>{p.name}</Typography.Text>
                  {p.isDefault ? <Tag color="processing" icon={<StarFilled />}>Default</Tag> : <Button size="small" type="text" icon={<StarOutlined />} onClick={() => update.mutateAsync({ id: p.id, isDefault: true }).catch((e) => message.error(errorMessage(e)))}>Make default</Button>}
                </Space>
              }
              subtitle={`${plural(p.stages.length, 'stage')} · ${plural(p.dealCount ?? 0, 'deal')}. Drag rows to reorder stages.`}
            />
          }
          extra={
            (p.dealCount ?? 0) > 0 ? (
              <Tooltip title="Move or delete its deals first">
                <Button size="small" danger icon={<DeleteOutlined />} disabled>Delete</Button>
              </Tooltip>
            ) : (
              <Popconfirm title={`Delete pipeline "${p.name}"?`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove.mutateAsync(p.id).then(() => message.success('Pipeline deleted')).catch((e) => message.error(errorMessage(e)))}>
                <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
              </Popconfirm>
            )
          }
        >
          <StagesEditor pipeline={p} />
        </Card>
      ))}
      <Modal title="New pipeline" open={newOpen} onCancel={() => setNewOpen(false)} onOk={() => form.submit()} okText="Create" confirmLoading={create.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => create.mutateAsync(v).then(() => setNewOpen(false)).catch((e) => message.error(errorMessage(e)))}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]} extra="Starts with New, Qualified, Proposal, Won and Lost; edit the stages afterwards.">
            <Input autoFocus placeholder="e.g. Enterprise sales" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function StagesEditor({ pipeline }: { pipeline: PipelineItem }) {
  const { message } = App.useApp();
  const add = useAddStage();
  const update = useUpdateStage();
  const reorder = useReorderStages();
  const remove = useDeleteStage();
  const [editing, setEditing] = useState<StageItem | 'new' | null>(null);
  const [deleting, setDeleting] = useState<StageItem | null>(null);
  const [moveTo, setMoveTo] = useState<string>();
  const [form] = Form.useForm<{ name: string; kind: 'OPEN' | 'WON' | 'LOST'; probability?: number | null; color?: string | null }>();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const rows = pipeline.stages;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(rows, rows.findIndex((r) => r.id === active.id), rows.findIndex((r) => r.id === over.id));
    reorder.mutateAsync({ pipelineId: pipeline.id, ids: next.map((r) => r.id) }).catch((e) => message.error(errorMessage(e)));
  };

  const save = async (v: { name: string; kind: 'OPEN' | 'WON' | 'LOST'; probability?: number | null; color?: string | null }) => {
    try {
      if (editing === 'new') await add.mutateAsync({ pipelineId: pipeline.id, ...v });
      else if (editing) await update.mutateAsync({ id: editing.id, ...v });
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <Table<StageItem>
            rowKey="id"
            dataSource={rows}
            pagination={false}
            components={{ body: { row: SortableRow } }}
            columns={[
              { key: 'drag', width: 32, render: () => <HolderOutlined aria-label="Drag to reorder" style={{ cursor: 'grab', color: '#999' }} /> },
              { title: 'Stage', dataIndex: 'name', render: (v, s) => <Space><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: s.color ?? '#d9d9d9' }} /><a onClick={() => { form.setFieldsValue({ name: s.name, kind: s.kind, probability: s.probability, color: s.color }); setEditing(s); }}>{v}</a></Space> },
              { title: 'Type', dataIndex: 'kind', width: 100, render: (k: StageItem['kind']) => <Tag color={KIND_COLOR[k]}>{KIND_LABEL[k]}</Tag> },
              { title: 'Probability', dataIndex: 'probability', width: 110, render: (v) => (v === null ? '—' : `${v}%`) },
              { title: 'Deals', dataIndex: 'dealCount', width: 80, render: (v) => v ?? 0 },
              { key: 'actions', width: 60, render: (_, s) => <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Delete stage ${s.name}`} title="Delete stage" onClick={() => { setMoveTo(undefined); setDeleting(s); }} /> },
            ]}
          />
        </SortableContext>
      </DndContext>
      <Button type="dashed" block icon={<PlusOutlined />} style={{ marginTop: 12 }} onClick={() => { form.resetFields(); form.setFieldsValue({ kind: 'OPEN', probability: 50 }); setEditing('new'); }}>
        Add stage
      </Button>

      <Modal title={editing === 'new' ? 'New stage' : 'Edit stage'} open={!!editing} onCancel={() => setEditing(null)} onOk={() => form.submit()} okText="Save" confirmLoading={add.isPending || update.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={save}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
          <Form.Item name="kind" label="Type" extra="Won and Lost stages close the deal when it lands in them.">
            <Select options={[{ value: 'OPEN', label: 'Open' }, { value: 'WON', label: 'Won' }, { value: 'LOST', label: 'Lost' }]} />
          </Form.Item>
          <Space size="large">
            <Form.Item name="probability" label="Probability (%)"><InputNumber min={0} max={100} /></Form.Item>
            <Form.Item name="color" label="Color" getValueFromEvent={(c) => (c ? (typeof c === 'string' ? c : c.toHexString()) : null)}>
              <ColorPicker allowClear format="hex" presets={[{ label: 'Presets', colors: ['#8c8c8c', '#1677ff', '#13c2c2', '#722ed1', '#fa8c16', '#52c41a', '#ff4d4f'] }]} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={`Delete stage "${deleting?.name ?? ''}"`}
        open={!!deleting}
        onCancel={() => setDeleting(null)}
        okText="Delete"
        okButtonProps={{ danger: true, disabled: !!deleting?.dealCount && !moveTo }}
        confirmLoading={remove.isPending}
        onOk={() => deleting && remove.mutateAsync({ id: deleting.id, moveDealsTo: moveTo }).then(() => setDeleting(null)).catch((e) => message.error(errorMessage(e)))}
        destroyOnHidden
      >
        {deleting?.dealCount ? (
          <>
            <Typography.Paragraph>This stage has {plural(deleting.dealCount, 'deal')}. Move them to:</Typography.Paragraph>
            <Select style={{ width: '100%' }} value={moveTo} onChange={setMoveTo} placeholder="Choose a stage" options={rows.filter((s) => s.id !== deleting.id).map((s) => ({ value: s.id, label: s.name }))} />
          </>
        ) : (
          <Typography.Paragraph>The stage is empty and will be removed.</Typography.Paragraph>
        )}
      </Modal>
    </>
  );
}
